"use client";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import dreamWorldProvider from "../../DreamWorldProvider";
import {
  selectFollowingPosts,
  selectHasMoreFollowingPosts,
  selectLastVisiblePost,
  addFollowingPosts,
  setHasMoreFollowingPosts,
  setLastVisiblePost,
} from "../../store/followingSlice";
import { selectUserGlobalData } from "../../store/dreamworldSlice";
import ContentCard from "./ContentCard";
import useIntersectionObserver from "../../hooks/useIntersectionObserver";
import { AppDispatch } from "../../store/store";
import { LibraryItem } from "../../types/library";
import Section from "../ui/Section";

interface FollowingPostsProps {
  onContentClick: (content: LibraryItem, source: "following") => void;
}

const FollowingPosts: React.FC<FollowingPostsProps> = ({ onContentClick }) => {
  const dispatch = useDispatch<AppDispatch>();
  const posts = useSelector(selectFollowingPosts);
  const hasMore = useSelector(selectHasMoreFollowingPosts);
  const lastDoc = useSelector(selectLastVisiblePost);
  const userGlobalData = useSelector(selectUserGlobalData);

  const [loading, setLoading] = useState(false);
  const [followingChunks, setFollowingChunks] = useState<string[][]>([]);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);

  const ref = useRef<HTMLDivElement>(null!);
  const entry = useIntersectionObserver(ref, {});
  const inView = !!entry?.isIntersecting;

  const fetchFollowing = useCallback(async () => {
    if (userGlobalData?.userData?.following) {
      const followingList = userGlobalData.userData.following;
      const chunks = [];
      for (let i = 0; i < followingList.length; i += 10) {
        chunks.push(followingList.slice(i, i + 10));
      }
      setFollowingChunks(chunks);
    }
  }, [userGlobalData?.userData?.following]);

  useEffect(() => {
    fetchFollowing();
  }, [fetchFollowing]);

  const fetchPosts = useCallback(async () => {
    if (loading || !hasMore || followingChunks.length === 0) return;

    setLoading(true);
    try {
      const currentChunk = followingChunks[currentChunkIndex];
      const { posts: newPosts, lastVisible } =
        await dreamWorldProvider.getFollowingPosts(
          currentChunk,
          lastDoc,
          userGlobalData?.userData?.uid
        );

      dispatch(addFollowingPosts(newPosts));
      dispatch(setLastVisiblePost(lastVisible));

      if (newPosts.length < 10) {
        if (currentChunkIndex < followingChunks.length - 1) {
          setCurrentChunkIndex(currentChunkIndex + 1);
          dispatch(setLastVisiblePost(null));
        } else {
          dispatch(setHasMoreFollowingPosts(false));
        }
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    hasMore,
    followingChunks,
    currentChunkIndex,
    lastDoc,
    userGlobalData?.userData?.uid,
    dispatch,
  ]);

  useEffect(() => {
    if (inView && hasMore) {
      fetchPosts();
    }
  }, [inView, hasMore, fetchPosts]);

  return (
    <Section title="Following posts">
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-1">
        {posts.map((post) => (
          <ContentCard
            key={post.id}
            item={post}
            onLike={() => {}}
            onClick={() => onContentClick(post, "following")}
            viewMode="grid"
          />
        ))}
      </div>
      {loading && <p className="text-center">Loading...</p>}
      {!hasMore && posts.length > 0 && (
        <p className="text-center">No more posts to show.</p>
      )}
      <div ref={ref} />
    </Section>
  );
};

export default FollowingPosts;
